import './App.css';
import React, { useEffect, useRef, useState } from 'react';
import LoginPage from './pages/LoginPage.js';
import Jobs from './pages/Jobs.js';
import Users from './pages/Users.js';
import ApiHandler from './pages/ApiHandler.js';
import SelfSettingsModal from './pages/SelfSettingsModal.js';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
    ThemeProvider, CssBaseline, AppBar, Toolbar, Tabs, Tab, Box, Typography, Button, Container,
    IconButton, Avatar, Menu, MenuItem, Divider, ListItemIcon, ListItemText, Tooltip
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import theme from './theme';

export const UserContext = React.createContext(null);
export const API = new ApiHandler();

function initials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('') || '?';
}

async function handleExportCsv() {
    const res = await API.downloadJobsCsv();
    if (!res?.success) alert('Export fehlgeschlagen: ' + (res?.msg || 'Unbekannter Fehler'));
}

function TopBar({ user, isAdmin, onLogout, onOpenProfile }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [anchor, setAnchor] = useState(null);

    const tabs = ['/'];
    if (isAdmin) tabs.push('/users');
    const current = tabs.includes(location.pathname) ? location.pathname : '/';

    return (
        <AppBar position="sticky">
            <Toolbar sx={{ gap: { xs: 1, sm: 2 }, px: { xs: 1.5, sm: 3 }, minHeight: { xs: 56, sm: 64 } }}>
                <Box
                    component="img"
                    src="/tvm-logo.png"
                    alt="TVM GmbH"
                    sx={{
                        height: { xs: 28, sm: 36 },
                        width: 'auto',
                        mr: { xs: 1, sm: 2 },
                        display: 'block'
                    }}
                />
                <Tabs
                    value={current}
                    onChange={(_, v) => navigate(v)}
                    textColor="inherit"
                    indicatorColor="primary"
                    variant="scrollable"
                    scrollButtons={false}
                    sx={{ flexGrow: 1, minHeight: { xs: 48, sm: 56 } }}
                >
                    <Tab value="/" label="Aufträge" />
                    {isAdmin && <Tab value="/users" label="Benutzer" />}
                </Tabs>

                <Tooltip title={user?.name ? `Angemeldet als ${user.name}` : 'Menü'}>
                    <IconButton onClick={(e) => setAnchor(e.currentTarget)} sx={{ p: 0.5 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: 14, fontWeight: 700 }}>
                            {initials(user?.name)}
                        </Avatar>
                    </IconButton>
                </Tooltip>
                <Menu
                    anchorEl={anchor}
                    open={!!anchor}
                    onClose={() => setAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Box sx={{ px: 2, py: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{user?.name || '—'}</Typography>
                        {user?.description && (
                            <Typography variant="caption" color="text.secondary">{user.description}</Typography>
                        )}
                    </Box>
                    <Divider />
                    <MenuItem onClick={() => { setAnchor(null); onOpenProfile(); }}>
                        <ListItemIcon><PersonOutlineIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Mein Profil</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { setAnchor(null); handleExportCsv(); }}>
                        <ListItemIcon><FileDownloadOutlinedIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Daten exportieren (CSV)</ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => { setAnchor(null); onLogout(); }}>
                        <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Abmelden</ListItemText>
                    </MenuItem>
                </Menu>
            </Toolbar>
        </AppBar>
    );
}

function AppContent() {
    const [loggedIn, setLoggedIn] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [loginMessage, setLoginMessage] = useState('');
    const selfModalRef = useRef(null);

    const refreshUser = () => setCurrentUser(API.user ? { ...API.user } : null);

    async function updateLoginState() {
        await API.getLoggedIn();
        setLoggedIn(API.loggedIn);
        refreshUser();
    }

    useEffect(() => { updateLoginState(); }, []);

    function handleLogin(user) {
        setLoginMessage('hide');
        API.login(user.username, user.password).then((res) => {
            if (!res.success) {
                setLoginMessage(res.msg);
            } else if (!API.loggedIn) {
                setLoginMessage('Anmeldung akzeptiert, aber Session wurde vom Browser nicht gespeichert. Cookies erlauben oder COOKIE_SECURE prüfen.');
            } else {
                setLoggedIn(true);
                refreshUser();
            }
        });
    }

    function handleLogout() {
        API.logout().then(() => {
            setLoggedIn(false);
            setCurrentUser(null);
            setLoginMessage('');
        });
    }

    function openProfile() {
        if (selfModalRef.current) selfModalRef.current.handleOpen();
    }

    if (!loggedIn) {
        return <LoginPage message={loginMessage} onLogin={handleLogin} />;
    }

    const isAdmin = Array.isArray(currentUser?.rights) && currentUser.rights.includes('admin');

    return (
        <Box sx={{ minHeight: '100vh' }}>
            <TopBar
                user={currentUser}
                isAdmin={isAdmin}
                onLogout={handleLogout}
                onOpenProfile={openProfile}
            />
            <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1.5, md: 3 } }}>
                <Routes>
                    <Route path="/" element={<Jobs />} />
                    <Route path="/users" element={isAdmin ? <Users /> : <Navigate to="/" replace />} />
                </Routes>
            </Container>
            <SelfSettingsModal modalRef={selfModalRef} onSaved={refreshUser} />
        </Box>
    );
}

function App() {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <BrowserRouter>
                <AppContent />
            </BrowserRouter>
        </ThemeProvider>
    );
}

export default App;
